import React, { useMemo } from 'react';

import { InstagramCreatorPost } from '@frontend/gql/social/graphql';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';

import { validateUrl } from '../../../../../../app/utils/url';
import { RecentPostsIcon } from '../../../../components/icons/recent-posts-icon';
import { RecentPostsNoBorderIcon } from '../../../../components/icons/recent-posts-no-border-icon';

interface RecentPostItemProps {
  username?: string;
  post: InstagramCreatorPost;
}

const RecentPostItem: React.FC<RecentPostItemProps> = ({ post, username }) => {
  const permalink = useMemo(() => {
    return validateUrl(post.permalink) ? post.permalink : '#';
  }, [post.permalink]);

  const caption = useMemo(() => {
    return post.caption || 'No caption';
  }, [post.caption]);

  const mediaUrl = useMemo(() => {
    return post.media?.thumbnailUrl || post.media?.url || null;
  }, [post.media]);

  return (
    <a
      key={post.id}
      href={permalink}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-[126px] h-[126px] rounded-[16px] overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-200 bg-muted"
      aria-label={`View post by ${username || 'creator'}`}
    >
      {mediaUrl ? (
        <img src={mediaUrl} alt={caption} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs bg-gray-100">
          <RecentPostsNoBorderIcon width={120} height={120} className="text-muted-foreground opacity-30" />
        </div>
      )}
    </a>
  );
};

interface RecentPostsSectionProps {
  username?: string;
  recentPosts: InstagramCreatorPost[];
  isLoading: boolean;
  maxPosts?: number;
}

export const RecentPostsSection: React.FC<RecentPostsSectionProps> = ({
  username,
  recentPosts,
  isLoading,
  maxPosts = 4,
}) => {
  if (isLoading) {
    return (
      <div className="p-4">
        <h3 className="text-md font-semibold mb-3 flex items-center gap-2">
          <div className="w-6 h-6 p-1 rounded-lg bg-[#96CA6C] flex items-center justify-center">
            <RecentPostsIcon />
          </div>
          <span>Recent Posts</span>
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[...Array(maxPosts)].map((_, i) => (
            <Skeleton key={i} className="w-[126px] h-[126px] rounded-[16px]" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h3 className="text-md font-semibold mb-3 flex items-center gap-2">
        <div className="w-6 h-6 p-1 rounded-lg bg-[#96CA6C] flex items-center justify-center">
          <RecentPostsIcon />
        </div>
        <span>Recent Posts</span>
      </h3>
      {recentPosts.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 justify-items-center">
          {recentPosts.map((post) => (
            <RecentPostItem key={post.id} post={post} username={username} />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 justify-items-center" aria-label="No recent posts">
            {[...Array(maxPosts)].map((_, i) => (
              <div
                key={i}
                className="w-[147px] h-[147px] rounded-[16px] bg-gray-100 flex items-center justify-center"
                aria-hidden="true"
              >
                <RecentPostsNoBorderIcon width={120} height={120} className="text-muted-foreground opacity-70" />
              </div>
            ))}
          </div>
          <div className="text-[#16282D] text-center font-['Inter'] text-base font-medium leading-7 mt-4">
            No data found.
          </div>
        </>
      )}
    </div>
  );
};

RecentPostsSection.displayName = 'RecentPostsSection';
